import { useState, useCallback, useEffect } from 'react';
import { notify } from '../../../../utils/toast';
import {
    connectInstagramAccount,
    InstagramOAuthRedirectPending,
    clearInstagramOAuthUrlParams,
    type InstagramModalData,
} from '../../../../services/modules/instagramService';
import { clearInstagramOAuthSession, hasPendingInstagramOAuthCallback } from '../../../../services/modules/instagramSDK';

interface UseInstagramAuthFlowReturn {
    isConnecting: boolean;
    authModalData: InstagramModalData | null;
    startAuthFlow: (organizationId: string) => Promise<void>;
    checkPendingAuth: () => void;
    clearAuthData: () => void;
}

const MODAL_KEY = 'adlitics_instagram_modal_data';

export function stashInstagramModal(data: InstagramModalData): void {
    sessionStorage.setItem(MODAL_KEY, JSON.stringify(data));
}

export function readInstagramModal(): InstagramModalData | null {
    const raw = sessionStorage.getItem(MODAL_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as InstagramModalData;
    } catch {
        sessionStorage.removeItem(MODAL_KEY);
        return null;
    }
}

export function clearInstagramModalStash(): void {
    sessionStorage.removeItem(MODAL_KEY);
}

export function useInstagramAuthFlow(): UseInstagramAuthFlowReturn {
    const [isConnecting, setIsConnecting] = useState(false);
    const [authModalData, setAuthModalData] = useState<InstagramModalData | null>(() => readInstagramModal());

    const finishWithModalData = useCallback((data: InstagramModalData) => {
        stashInstagramModal(data);
        clearInstagramOAuthSession();
        setAuthModalData(data);
    }, []);

    const startAuthFlow = useCallback(async (organizationId: string) => {
        if (!organizationId) {
            notify.error('Organização não encontrada');
            return;
        }

        setIsConnecting(true);
        sessionStorage.setItem('adlitics_instagram_connecting', 'true');

        let redirecting = false;
        try {
            const data = await connectInstagramAccount(organizationId);
            finishWithModalData(data);
        } catch (error: unknown) {
            if (error instanceof InstagramOAuthRedirectPending) {
                redirecting = true;
                return;
            }
            console.error('[useInstagramAuthFlow]', error);
            clearInstagramOAuthSession();
            notify.error(error instanceof Error ? error.message : 'Erro ao conectar Instagram');
        } finally {
            if (!redirecting) setIsConnecting(false);
        }
    }, [finishWithModalData]);

    const checkPendingAuth = useCallback(() => {
        if (hasPendingInstagramOAuthCallback()) return;
        if (readInstagramModal()) return;
        if (sessionStorage.getItem('adlitics_instagram_connecting') === 'true') {
            clearInstagramOAuthSession();
            setIsConnecting(false);
        }
    }, []);

    const clearAuthData = useCallback(() => {
        setAuthModalData(null);
        clearInstagramModalStash();
        clearInstagramOAuthUrlParams();
    }, []);

    useEffect(() => {
        const stashed = readInstagramModal();
        if (stashed) setAuthModalData(stashed);
    }, []);

    // Volta do Facebook (botão voltar / bfcache) sem code — libera UI
    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted || sessionStorage.getItem('adlitics_instagram_connecting') === 'true') {
                checkPendingAuth();
            }
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, [checkPendingAuth]);

    return {
        isConnecting,
        authModalData,
        startAuthFlow,
        checkPendingAuth,
        clearAuthData,
    };
}
